import React, { useState, useEffect } from 'react';
import { Anchor, Compass, PlusCircle, Upload, BarChart3, Ship } from 'lucide-react';
import { getRequest, postRequest } from '../../services/apiService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomDropdown from './CustomDropdown';
import ShipCard from './ShipCard';
import UploadModal from './UploadModal';
import ComparisonSetup from './ComparisonSetup';
import ComparativeAnalysis from './ComparativeAnalysis';
import SCurveAnalysisSection from './SCurveAnalysisSection';
import ReusableForm from '../../common/ReusableForm';
import BarLoader from './BarLoader'; // Import BarLoader if it's used directly for upload/add

const LegacyNavalSystem = () => {
  const [selectedClass, setSelectedClass] = useState('');
  const [firstCompareProject, setFirstCompareProject] = useState('');
  const [firstCompareShip, setFirstCompareShip] = useState('');
  const [secondCompareProject, setSecondCompareProject] = useState('');
  const [secondCompareShip, setSecondCompareShip] = useState('');
  const [firstCompareShipsOptions, setFirstCompareShipsOptions] = useState([]);
  const [secondCompareShipsOptions, setSecondCompareShipsOptions] = useState([]);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [isLoadingCompare, setIsLoadingCompare] = useState(false);
  const [compareLoaderMessage, setCompareLoaderMessage] = useState('');
  const [compareProgress, setCompareProgress] = useState(0);
  const [selectedTask1, setSelectedTask1] = useState(null);
  const [selectedTask2, setSelectedTask2] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [projects, setProjects] = useState([]); // This will now hold all projects, not status filtered ones
  const [firstCompareProjectsOptions, setFirstCompareProjectsOptions] = useState([]); // New state for status-filtered projects
  const [secondCompareProjectsOptions, setSecondCompareProjectsOptions] = useState([]); // New state for status-filtered projects

  const [classes, setClasses] = useState([]);
  const [uploadModalShips, setUploadModalShips] = useState([]);
  const [selectedUploadProject, setSelectedUploadProject] = useState('');
  const [selectedUploadShip, setSelectedUploadShip] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isLoadingUpload, setIsLoadingUpload] = useState(false);
  const [uploadLoaderMessage, setUploadLoaderMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentScurveView, setCurrentScurveView] = useState(0);
  const [sCurveData1, setSCurveData1] = useState(null);
  const [sCurveData2, setSCurveData2] = useState(null);
  const [combinedSCurveData, setCombinedSCurveData] = useState(null);
  const [firstProjectStatus, setFirstProjectStatus] = useState('');
  const [secondProjectStatus, setSecondProjectStatus] = useState('');
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [addProjectOrShipFormData, setAddProjectOrShipFormData] = useState({});
  const [isAddingProjectOrShip, setIsAddingProjectOrShip] = useState(false);
  const [addProjectOrShipLoaderMessage, setAddProjectOrShipLoaderMessage] = useState('');
  const [addProjectOrShipProgress, setAddProjectOrShipProgress] = useState(0);
  const [shipyards, setShipyards] = useState([]);
  const [formType, setFormType] = useState('project');

  const projectStatusOptions = [
    { value: 'ongoing', label: 'ongoing' },
    { value: 'completed', label: 'completed' },
  ];

  const transformSCurveData = (apiData, shipName) => {
  if (!apiData) return null;

  // Group by date and collect all progress values
  const progressByDate = {};
  apiData.actual_data.forEach(item => {
    const date = item[0];
    const progress = item[1];
    if (!progressByDate[date]) {
      progressByDate[date] = [];
    }
    progressByDate[date].push(progress);
  });

  // Create an array of data points for each date
  const actualData = [];
  Object.keys(progressByDate).forEach(date => {
    progressByDate[date].forEach(progress => {
      actualData.push({
        date: date,
        cumulative_progress_percent: progress,
        type: 'actual'
      });
    });
  });

  return {
    name: shipName,
    actual: actualData
  };
};

  const combineSCurveData = (data1, data2) => {
  if (!data1 || !data2) return null;

  // Collect all dates from both datasets
  const allDates = Array.from(new Set([
    ...data1.actual.map(d => d.date),
    ...data2.actual.map(d => d.date)
  ]));

  return {
    labels: allDates,
    datasets: [
      {
        label: `${data1.name} Actual`,
        data: data1.actual.map(d => ({
          x: d.date,
          y: d.cumulative_progress_percent
        })),
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        tension: 0.4,
        fill: false
      },
      {
        label: `${data2.name} Actual`,
        data: data2.actual.map(d => ({
          x: d.date,
          y: d.cumulative_progress_percent
        })),
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        tension: 0.4,
        fill: false
      }
    ]
  };
};

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const classesResponse = await getRequest('/master/classes/');
        if (classesResponse?.data) {
          setClasses(classesResponse.data.map(c => ({ value: c.id.toString(), label: c.name })));
        }
        const shipyardsResponse = await getRequest('/master/shipyards/');
        if (shipyardsResponse?.data) {
          setShipyards(shipyardsResponse.data.map(s => ({ value: s.id.toString(), label: s.name })));
        }
        // Fetch all projects initially for the "Add New Ship" form dropdown
        const allProjectsResponse = await getRequest(`/master/projects/`);
        if (allProjectsResponse?.data) {
            setProjects(allProjectsResponse.data.map(p => ({ value: p.id.toString(), label: p.name })));
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast.error("Failed to fetch initial data.");
      }
    };
    fetchInitialData();
  }, []);

  const fetchProjectsAndShipsByStatus = async (classId, status, targetSetter) => {
    if (!classId || !status) {
      targetSetter([]);
      return;
    }
    try {
      const response = await getRequest(`/transaction/project-status-filter/?status=${status}&class_name=${classId}`);
      if (response?.data) {
        // Transform the response to populate project and then ship options
        const projectsData = response.data.map(p => ({ value: p.id.toString(), label: p.name, ships: p.ships }));
        targetSetter(projectsData);
      } else {
        targetSetter([]);
      }
    } catch (error) {
      console.error("Error fetching projects by status:", error);
      toast.error("Failed to fetch projects for selected status.");
      targetSetter([]);
    }
  };

  // Effect to handle changes in selectedClass and firstProjectStatus for first comparison side
  useEffect(() => {
    if (selectedClass && firstProjectStatus) {
      fetchProjectsAndShipsByStatus(selectedClass, firstProjectStatus, (data) => {
        setFirstCompareProjectsOptions(data);
        setFirstCompareProject(''); // Reset selected project when status changes
        setFirstCompareShipsOptions([]); // Reset ships
      });
    } else {
      setFirstCompareProjectsOptions([]);
      setFirstCompareProject('');
      setFirstCompareShipsOptions([]);
    }
  }, [selectedClass, firstProjectStatus]);

  // Effect to handle changes in selectedClass and secondProjectStatus for second comparison side
  useEffect(() => {
    if (selectedClass && secondProjectStatus) {
      fetchProjectsAndShipsByStatus(selectedClass, secondProjectStatus, (data) => {
        setSecondCompareProjectsOptions(data);
        setSecondCompareProject(''); // Reset selected project when status changes
        setSecondCompareShipsOptions([]); // Reset ships
      });
    } else {
      setSecondCompareProjectsOptions([]);
      setSecondCompareProject('');
      setSecondCompareShipsOptions([]);
    }
  }, [selectedClass, secondProjectStatus]);

  const updateShipOptions = (projectId, projectsOptions, setShipOptions) => {
    const selectedProject = projectsOptions.find(p => p.value === projectId);
    if (selectedProject && selectedProject.ships) {
      setShipOptions(selectedProject.ships.map(s => ({ value: s.id.toString(), label: s.name })));
    } else {
      setShipOptions([]);
    }
  };


  const fetchUploadModalShips = async (projectId) => {
    if (!projectId) {
      setUploadModalShips([]);
      return;
    }
    try {
      const response = await getRequest(`/master/projects/?parent_id=${projectId}`);
      if (response?.data) {
        setUploadModalShips(response.data.map(ship => ({ value: ship.id.toString(), label: ship.name })));
      }
    } catch (error) {
      console.error("Error fetching upload modal ships:", error);
      toast.error("Failed to fetch ships for upload modal.");
      setUploadModalShips([]);
    }
  };

  // The existing fetchFirstCompareShips and fetchSecondCompareShips will be replaced by the logic in updateShipOptions
  // and the projects will come from firstCompareProjectsOptions/secondCompareProjectsOptions

  const handleOpenUploadModal = () => {
    setShowUploadModal(true);
    setSelectedUploadProject('');
    setSelectedUploadShip('');
    setUploadedFile(null);
    setUploadModalShips([]);
    setIsLoadingUpload(false);
    setUploadLoaderMessage('');
    setUploadProgress(0);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setIsLoadingUpload(false);
    setUploadLoaderMessage('');
    setUploadProgress(0);
  };

  const handleFileChange = (event) => {
    setUploadedFile(event.target.files[0]);
  };

  const handleUploadFile = async (e) => {
    e.preventDefault();

    if (!uploadedFile) {
      toast.error('Please select a file to upload.');
      return;
    }

    if (!selectedUploadShip) {
      toast.error('Ship is required for upload.');
      return;
    }

    setIsLoadingUpload(true);
    setUploadLoaderMessage('Preparing file for upload...');
    setUploadProgress(10);

    await new Promise(resolve => setTimeout(resolve, 1000));
    setUploadLoaderMessage('Compressing and encrypting data...');
    setUploadProgress(30);

    await new Promise(resolve => setTimeout(resolve, 1000));
    setUploadLoaderMessage('Uploading file to server...');
    setUploadProgress(60);

    await new Promise(resolve => setTimeout(resolve, 1500));
    setUploadProgress(90);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile, uploadedFile.name);
      formData.append('project_id', selectedUploadShip);

      const pertResponse = await postRequest(`pert/upload-pert/`, formData, true);

      setUploadProgress(100);
      setUploadLoaderMessage('File uploaded successfully!');
      await new Promise(resolve => setTimeout(resolve, 1500));

      toast.success(`File "${uploadedFile.name}" uploaded successfully for Ship ID: ${selectedUploadShip}`);

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || 'File upload failed.';
      setUploadLoaderMessage(`Upload failed: ${errorMessage}`);
      setUploadProgress(0);
      await new Promise(resolve => setTimeout(resolve, 2500));
      toast.error(`Upload failed: ${errorMessage}`);

    } finally {
      setIsLoadingUpload(false);
      setUploadLoaderMessage('');
      setUploadProgress(0);
      handleCloseUploadModal();
    }
  };

  const handleCompareShips = async () => {
    if (!firstCompareShip || !secondCompareShip) {
      toast.error('Please select both ships for comparison.');
      return;
    }

    setIsLoadingCompare(true);
    setCompareLoaderMessage('Fetching comparison data...');
    setCompareProgress(10);

    try {
      const payload = {
        "project1": parseInt(firstCompareShip),
        "project2": parseInt(secondCompareShip)
      };

      const response = await postRequest('/pert/compare-task/', payload);
      setComparisonResults(response.data);

      setCompareLoaderMessage('Fetching S-Curve data...');
      setCompareProgress(50);

      const scurveResponse = await getRequest(
        `transaction/fetch-project-s-curve/?project1=${firstCompareShip}&project2=${secondCompareShip}`
      );

      if (scurveResponse.data) {
        const ship1Name = response.data.task_transactions1?.[0]?.ship_name || 'Ship 1';
        const ship2Name = response.data.task_transactions2?.[0]?.ship_name || 'Ship 2';

        const transformedData1 = transformSCurveData(scurveResponse.data.project1, ship1Name);
        const transformedData2 = transformSCurveData(scurveResponse.data.project2, ship2Name);

        setSCurveData1(transformedData1);
        setSCurveData2(transformedData2);
        setCombinedSCurveData(combineSCurveData(transformedData1, transformedData2));
      }

      setCompareProgress(100);
      setCompareLoaderMessage('Comparison complete!');
      await new Promise(resolve => setTimeout(resolve, 1500));

    } catch (error) {
      console.error('Comparison error:', error);
      const errorMessage = error.response?.data?.detail || 'Ship comparison failed.';
      setCompareLoaderMessage(`Comparison failed: ${errorMessage}`);
      setCompareProgress(0);
      await new Promise(resolve => setTimeout(resolve, 2500));
      setComparisonResults(null);
      toast.error(`Comparison failed: ${errorMessage}`);
    } finally {
      setIsLoadingCompare(false);
      setCompareLoaderMessage('');
      setCompareProgress(0);
    }
  };

  const handleNextScurveView = () => {
    setCurrentScurveView((prev) => (prev + 1) % 3);
  };

  const handlePrevScurveView = () => {
    setCurrentScurveView((prev) => (prev - 1 + 3) % 3);
  };

  const handleOpenAddProjectModal = (type) => {
    setFormType(type);
    setAddProjectOrShipFormData({});
    setShowAddProjectModal(true);
  };

  const handleCloseAddProjectModal = () => {
    setShowAddProjectModal(false);
    setIsAddingProjectOrShip(false);
    setAddProjectOrShipLoaderMessage('');
    setAddProjectOrShipProgress(0);
  };

  const addProjectFields = [
    { name: 'projectName', label: 'Project Name', type: 'text', required: true },
    { name: 'projectCode', label: 'Project Code', type: 'text', required: true },
    { name: 'class', label: 'Class', type: 'select', options: classes, required: true },
  ];

  const addShipFields = [
    { name: 'shipName', label: 'Ship Name', type: 'text', required: true },
    { name: 'startDate', label: 'Start Date', type: 'date', required: true },
    { name: 'endDate', label: 'End Date', type: 'date', required: true },
    { name: 'yard', label: 'Yard', type: 'text', required: true },
    { name: 'shipyard', label: 'Shipyard', type: 'select', options: shipyards, required: true },
    { name: 'project', label: 'Project', type: 'select', options: projects, required: true },
  ];

  const handleAddProjectOrShipSubmit = async (e) => {
    e.preventDefault();

    setIsAddingProjectOrShip(true);
    setAddProjectOrShipProgress(10);
    setAddProjectOrShipLoaderMessage(`Adding new ${formType}...`);

    await new Promise(resolve => setTimeout(resolve, 1000));
    setAddProjectOrShipProgress(30);

    try {
      let payload;
      let endpoint;
      let successMessage;

      if (formType === 'project') {
        payload = {
          name: addProjectOrShipFormData.projectName,
          code: addProjectOrShipFormData.projectCode,
          class_name: parseInt(addProjectOrShipFormData.class),
          legacy: true,
        };
        endpoint = '/master/projects/';
        successMessage = `Project "${addProjectOrShipFormData.projectName}" added successfully!`;
      } else if (formType === 'ship') {
        payload = {
          name: addProjectOrShipFormData.shipName,
          start_date: addProjectOrShipFormData.startDate,
          end_date: addProjectOrShipFormData.endDate,
          yard: addProjectOrShipFormData.yard,
          shipyard: parseInt(addProjectOrShipFormData.shipyard),
          parent: parseInt(addProjectOrShipFormData.project),
          legacy: true,
        };
        endpoint = '/master/projects/';
        successMessage = `Ship "${addProjectOrShipFormData.shipName}" added successfully!`;
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      setAddProjectOrShipProgress(60);

      const response = await postRequest(endpoint, payload);

      setAddProjectOrShipProgress(100);
      setAddProjectOrShipLoaderMessage(successMessage);
      await new Promise(resolve => setTimeout(resolve, 1500));

      toast.success(successMessage);
      handleCloseAddProjectModal();

      if (formType === 'project') {
        // Re-fetch classes and all projects after adding a new project
        const classesResponse = await getRequest('/master/classes/');
        if (classesResponse?.data) {
          setClasses(classesResponse.data.map(c => ({ value: c.id.toString(), label: c.name })));
        }
        const allProjectsResponse = await getRequest(`/master/projects/`);
        if (allProjectsResponse?.data) {
            setProjects(allProjectsResponse.data.map(p => ({ value: p.id.toString(), label: p.name })));
        }
      } else if (formType === 'ship' && selectedUploadProject) {
        // If a ship was added and a project is selected in the upload modal, refresh its ships
        fetchUploadModalShips(selectedUploadProject);
      }

    } catch (error) {
      console.error(`Add ${formType} error:`, error);
      const errorMessage = error.response?.data?.detail || `Failed to add ${formType}.`;
      setAddProjectOrShipLoaderMessage(`Failed: ${errorMessage}`);
      setAddProjectOrShipProgress(0);
      await new Promise(resolve => setTimeout(resolve, 2500));
      toast.error(`Failed to add ${formType}: ${errorMessage}`);
    } finally {
      setIsAddingProjectOrShip(false);
      setAddProjectOrShipLoaderMessage('');
      setAddProjectOrShipProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes fade-in-up {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out;
        }

        .animation-delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-32 h-32 bg-blue-100 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute bottom-32 right-32 w-24 h-24 bg-indigo-100 rounded-full opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-cyan-100 rounded-full opacity-20 animate-pulse delay-2000"></div>
      </div>

      <header className="relative z-10 px-6 py-6 bg-white shadow-sm border-b border-blue-100">
        <div className="max-w-full mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                <Anchor className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  LEGACY
                </h1>
                <p className="text-gray-600 text-sm">Naval Operations Management System</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <Compass className="w-5 h-5 text-blue-500" />
              <span className="text-sm">Maritime Command Center</span>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 px-6 py-8">
        <div className="max-w-full mx-auto">
          {/* Moved PERT Chart Upload and Add Project/Ship sections here */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-blue-100 mb-8">
             <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-500" />
                Management Actions
             </h2>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 bg-blue-50 rounded-xl border border-blue-200">
                  <h3 className="text-gray-800 font-medium mb-4 flex items-center">
                    <Upload className="w-4 h-4 mr-2 text-blue-500" />
                    PERT Chart Upload
                  </h3>
                  <div className="flex items-center space-x-4">
                    <button
                      className="flex-1 flex items-center justify-center h-12 px-6 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                      onClick={handleOpenUploadModal}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload PERT Chart
                    </button>
                  </div>
                </div>

                <div className="p-6 bg-blue-50 rounded-xl border border-blue-200">
                  <h3 className="text-gray-800 font-medium mb-4 flex items-center">
                    <PlusCircle className="w-4 h-4 mr-2 text-blue-500" />
                    Add Project/Ship
                  </h3>
                  <div className="flex items-center space-x-4">
                    <button
                      className="flex-1 flex items-center justify-center h-12 px-6 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 rounded-xl text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                      onClick={() => handleOpenAddProjectModal('project')}
                    >
                      <PlusCircle className="w-4 h-4 mr-2" />
                      Add New Project
                    </button>
                    <button
                      className="flex-1 flex items-center justify-center h-12 px-6 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 rounded-xl text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                      onClick={() => handleOpenAddProjectModal('ship')}
                    >
                      <Ship className="w-4 h-4 mr-2" /> {/* Assuming Ship icon is imported or needs to be */}
                      Add New Ship
                    </button>
                  </div>
                </div>
            </div>
          </div>

          <ComparisonSetup
            selectedClass={selectedClass}
            setSelectedClass={(value) => {
              setSelectedClass(value);
              setFirstCompareProject('');
              setFirstCompareShip('');
              setSecondCompareProject('');
              setSecondCompareShip('');
              setFirstProjectStatus('');
              setSecondProjectStatus('');
              setFirstCompareProjectsOptions([]); // Clear project options on class change
              setSecondCompareProjectsOptions([]); // Clear project options on class change
            }}
            firstCompareProject={firstCompareProject}
            setFirstCompareProject={(value) => {
              setFirstCompareProject(value);
              updateShipOptions(value, firstCompareProjectsOptions, setFirstCompareShipsOptions);
              setFirstCompareShip(''); // Reset ship when project changes
            }}
            firstCompareShip={firstCompareShip}
            setFirstCompareShip={setFirstCompareShip}
            secondCompareProject={secondCompareProject}
            setSecondCompareProject={(value) => {
              setSecondCompareProject(value);
              updateShipOptions(value, secondCompareProjectsOptions, setSecondCompareShipsOptions);
              setSecondCompareShip(''); // Reset ship when project changes
            }}
            secondCompareShip={secondCompareShip}
            setSecondCompareShip={setSecondCompareShip}
            firstProjectStatus={firstProjectStatus}
            setFirstProjectStatus={setFirstProjectStatus}
            secondProjectStatus={secondProjectStatus}
            setSecondProjectStatus={setSecondProjectStatus}
            handleCompareShips={handleCompareShips}
            // Remove handleOpenUploadModal and handleOpenAddProjectModal from here
            isLoadingCompare={isLoadingCompare}
            compareLoaderMessage={compareLoaderMessage}
            compareProgress={compareProgress}
            comparisonResults={comparisonResults}
            selectedTask1={selectedTask1}
            setSelectedTask1={setSelectedTask1}
            selectedTask2={selectedTask2}
            setSelectedTask2={setSelectedTask2}
            classes={classes}
            projects={projects} // This is for Add New Ship form
            firstCompareProjectsOptions={firstCompareProjectsOptions} // Pass filtered projects for first side
            secondCompareProjectsOptions={secondCompareProjectsOptions} // Pass filtered projects for second side
            firstCompareShipsOptions={firstCompareShipsOptions}
            secondCompareShipsOptions={secondCompareShipsOptions}
            projectStatusOptions={projectStatusOptions}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-4 h-4 bg-blue-500 rounded-full mr-3"></div>
                <h2 className="text-lg font-semibold text-gray-800">First Ship Overview</h2>
              </div>
              <ShipCard
                shipData={comparisonResults?.task_transactions1}
                side="First"
                color="border-blue-300"
              />
            </div>

            <div>
              <div className="flex items-center mb-4">
                <div className="w-4 h-4 bg-indigo-500 rounded-full mr-3"></div>
                <h2 className="text-lg font-semibold text-gray-800">Second Ship Overview</h2>
              </div>
              <ShipCard
                shipData={comparisonResults?.task_transactions2}
                side="Second"
                color="border-indigo-300"
              />
            </div>
          </div>

          <ComparativeAnalysis comparisonResults={comparisonResults} />

          <SCurveAnalysisSection
            currentScurveView={currentScurveView}
            handleNextScurveView={handleNextScurveView}
            handlePrevScurveView={handlePrevScurveView}
            setCurrentScurveView={setCurrentScurveView}
            sCurveData1={sCurveData1}
            sCurveData2={sCurveData2}
            combinedSCurveData={combinedSCurveData}
            comparisonResults={comparisonResults}
          />
        </div>
      </main>

      <UploadModal
        showUploadModal={showUploadModal}
        handleCloseUploadModal={handleCloseUploadModal}
        selectedUploadProject={selectedUploadProject}
        setSelectedUploadProject={setSelectedUploadProject}
        selectedUploadShip={selectedUploadShip}
        setSelectedUploadShip={setSelectedUploadShip}
        uploadedFile={uploadedFile}
        handleFileChange={handleFileChange}
        handleUploadFile={handleUploadFile}
        isLoadingUpload={isLoadingUpload}
        uploadLoaderMessage={uploadLoaderMessage}
        uploadProgress={uploadProgress}
        projects={projects} // This is for Upload Modal projects
        uploadModalShips={uploadModalShips}
        fetchUploadModalShips={fetchUploadModalShips}
      />

      {showAddProjectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <ReusableForm
            formData={addProjectOrShipFormData}
            setFormData={setAddProjectOrShipFormData}
            fields={formType === 'project' ? addProjectFields : addShipFields}
            onSubmit={handleAddProjectOrShipSubmit}
            onHide={handleCloseAddProjectModal}
            loading={isAddingProjectOrShip}
            submitLabel={isAddingProjectOrShip ? `Adding ${formType}...` : `Add ${formType}`}
            headerText={formType === 'project' ? 'Add New Project' : 'Add New Ship'}
          />
        </div>
      )}
    </div>
  );
};

export default LegacyNavalSystem;